"use client";

import {useCallback, useEffect, useRef} from "react";
import {ImageType, resolveVadImageUrl} from "vadimages-nextjs-image-optimizer";
import {launchClip, launchPoster} from "./LaunchVideo";
import {prefersReducedMotion} from "./useMission";

const IMG = '/images/mjbc-2026';
const MEDIA = 'https://images.active710.com/video/mjbc-2026';

/**
 * Read from the same place VadImage reads it, so the two can never drift: these
 * are inlined into the client bundle by next.config.ts's `env` block. The
 * fallback is only for a bundler that skipped the substitution.
 */
const VAD_SIZES = (process.env.vadImage_imagesSizes ?? '480,787,1024,1440')
    .split(',')
    .map(Number);

export type MissionStage = 'save-the-date' | 'formal-invite';

/**
 * `weight` is a measured transfer size in KB, and only the ratios matter.
 *
 * Counting assets instead would make the bar lie: the rocket clip is 1.9-2.7MB
 * and everything else on the page put together is under 350KB, so an evenly
 * weighted bar would sit at 90% while the one thing that can actually stall the
 * sequence had barely started.
 */
type Asset =
    | {kind: 'image' | 'video'; url: string; weight: number}
    | {kind: 'scripts' | 'fonts'; weight: number};

/**
 * Small on purpose. The page's own scripts and its webfont belong in "loaded",
 * but window.load also waits on analytics and the consent banner — third
 * parties this page does not control. Weighted like this, one of them hanging
 * costs the bar a percent instead of parking it just short of full.
 */
const SCRIPTS_WEIGHT = 30;
const FONTS_WEIGHT = 30;

/**
 * The variant VadImage will actually request at this viewport.
 *
 * It emits one <source> per size per pixel ratio, all sharing the same `media`
 * query with the ratio-1 candidate written first — and a browser takes the
 * FIRST matching <source> — so the 2x candidates never win and the bucket is
 * decided by viewport width alone. Guessing any other URL here would download a
 * second copy rather than warm the one the page goes on to use.
 */
function vadUrl(src: string): string {
    const size = VAD_SIZES.find(candidate => candidate >= window.innerWidth)
        ?? Math.max(...VAD_SIZES);

    return resolveVadImageUrl(src, {
        size,
        pixelRatio: 1,
        format: ImageType.WEBP,
        useOnlyOneDomain: true,
    });
}

/**
 * Everything the sequence needs to reach its payoff screen — the briefing —
 * without a hitch.
 *
 * Deliberately stops there rather than covering all thirteen frames. The screens
 * past the briefing are gated on the visitor reading and clicking, which buys
 * far more time than a loading bar ever could, and the form's own backdrop is
 * another 214KB that nobody should wait on up front.
 */
function missionAssets(stage: MissionStage): Asset[] {
    // Matches the sheet's own breakpoint and LaunchVideo's variant pick.
    const portrait = window.matchMedia('(max-width: 900px)').matches;
    const variant = portrait ? '9x16' : '16x9';

    const assets: Asset[] = [
        {kind: 'scripts', weight: SCRIPTS_WEIGHT},
        {kind: 'fonts', weight: FONTS_WEIGHT},

        // Vector, and the first thing to appear once the bar clears.
        {kind: 'image', url: `${IMG}/logo-active-x-small-circle.svg`, weight: 8},
        // Light for what it covers, because the grain came out of it and moved
        // into CSS — see .backdrop::before. It used to be 164 on desktop.
        {kind: 'image', url: vadUrl(`${IMG}/bg-teal-gradient-v2.png`), weight: portrait ? 4 : 20},
        {kind: 'image', url: vadUrl(`${IMG}/ignition-sequence-badge.png`), weight: portrait ? 69 : 99},
        {kind: 'image', url: vadUrl(`${IMG}/icon-calendar.svg`), weight: 1},
        {kind: 'image', url: vadUrl(`${IMG}/icon-arrow-right.svg`), weight: 1},

        // The poster carries the launch's first frame for everyone, and stands
        // in for the whole clip under reduced motion.
        {kind: 'image', url: launchPoster(variant), weight: portrait ? 59 : 95},

        /*
         * Frame 9's backdrop. Both sizes now load the client's own plate rather
         * than the vector, so desktop's weight goes from the 5KB of an SVG to
         * the 229 of a full composite — it is the heaviest thing on the page
         * after the launch clip and the badge, and the bar has to say so.
         */
        portrait
            ? {
                kind: 'image',
                url: stage === 'formal-invite'
                    ? `${MEDIA}-line-bg-invite-mobile-v2.webp`
                    : `${MEDIA}-line-bg-mobile.jpg`,
                weight: stage === 'formal-invite' ? 175 : 169,
            }
            : {kind: 'image', url: `${MEDIA}-line-bg-desktop-v2.webp`, weight: 229},
    ];

    // Reduced motion never requests the clip — it gets the poster, already
    // listed above — so it must not be made to wait for one.
    if (!prefersReducedMotion()) {
        // Measured on the sound cut: 285KB of that is the audio track the silent
        // encode did not carry, and the video bitrate is unchanged from it.
        assets.push({
            kind: 'video',
            url: launchClip(variant),
            weight: portrait ? 2096 : 2971,
        });
    }

    return assets;
}

/** Resolves as soon as the image is in cache, and also if it never arrives. */
function preloadImage(url: string, done: () => void): () => void {
    const img = new Image();

    img.onload = done;
    // A missing asset must not hold the bar. The page will render it or it
    // will not; either way the visitor should not be kept on a loading screen
    // waiting for a file that is already a 404.
    img.onerror = done;
    img.src = url;
    // Some browsers fire nothing at all for an image served from cache.
    if (img.complete) done();

    return () => {
        img.onload = null;
        img.onerror = null;
    };
}

/**
 * Pulls the clip down ahead of time and reports how far it has got.
 *
 * A detached element rather than fetch(): the R2 bucket sends no
 * Access-Control-Allow-Origin, so a fetch would fail CORS outright and a
 * no-cors one would hand back an opaque body with no readable progress. A media
 * element needs no CORS at all, and its `buffered` ranges give the only genuine
 * byte-ish progress on the page.
 *
 * The bytes are left in the HTTP cache — the response is `immutable` — so the
 * real <video> in LaunchVideo mounts against a warm cache instead of starting
 * a 2.7MB download at the moment the rocket is supposed to fly.
 */
function preloadVideo(url: string, report: (ratio: number) => void, done: () => void): () => void {
    const video = document.createElement('video');
    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;

    const measure = () => {
        const {buffered, duration} = video;
        if (!duration || buffered.length === 0) return;
        // Ranges are seconds, not bytes. Near enough on a single fixed-bitrate
        // clip, and the alternative reads nothing at all.
        report(Math.min(1, buffered.end(buffered.length - 1) / duration));
    };

    // `suspend` means the browser has stopped fetching on purpose, which under
    // preload="auto" usually means satisfied — but it also fires early on some
    // browsers, so it only counts once the element says it has enough to play
    // through. Otherwise it is just another progress reading.
    const settle = () => (video.readyState >= video.HAVE_ENOUGH_DATA ? done() : measure());

    video.addEventListener('progress', measure);
    video.addEventListener('loadedmetadata', measure);
    video.addEventListener('suspend', settle);
    video.addEventListener('canplaythrough', done);
    video.addEventListener('error', done);

    video.src = url;
    video.load();

    return () => {
        video.removeEventListener('progress', measure);
        video.removeEventListener('loadedmetadata', measure);
        video.removeEventListener('suspend', settle);
        video.removeEventListener('canplaythrough', done);
        video.removeEventListener('error', done);
        // Leaving the page mid-download should not keep the connection open.
        video.removeAttribute('src');
        video.load();
    };
}

/** window.load — the page's own scripts, styles and inline assets. */
function preloadScripts(done: () => void): () => void {
    if (document.readyState === 'complete') {
        done();
        return () => undefined;
    }

    window.addEventListener('load', done, {once: true});
    return () => window.removeEventListener('load', done);
}

/**
 * Erbaum. Loaded with `preload: false` in (event)/layout.tsx, so the faces only
 * start arriving once the boot label paints — which is exactly when this runs.
 */
function preloadFonts(done: () => void): () => void {
    if (typeof document.fonts === 'undefined') {
        done();
        return () => undefined;
    }

    let live = true;
    document.fonts.ready.then(() => {
        if (live) done();
    });

    return () => {
        live = false;
    };
}

/**
 * How much of the mission has actually arrived, 0-1.
 *
 * Returns a getter rather than state, on purpose: the boot bar reads this from
 * inside a requestAnimationFrame loop, and a video mid-download fires `progress`
 * several times a second. Held in state, every one of those would re-render the
 * whole sequence for a number that only one element consumes — the same reason
 * the bar's own width is written straight to the node.
 */
export function useMissionProgress(stage: MissionStage): () => number {
    const loaded = useRef<number[]>([]);
    const total = useRef(0);

    useEffect(() => {
        const assets = missionAssets(stage);
        // Captured locally as well as in the ref so the closures below keep
        // writing to their own array if the effect ever re-runs.
        const progress = new Array<number>(assets.length).fill(0);

        loaded.current = progress;
        total.current = assets.reduce((sum, asset) => sum + asset.weight, 0);

        const teardown = assets.map((asset, index) => {
            const done = () => {
                progress[index] = asset.weight;
            };
            // Never let a reading go backwards — `buffered` can drop a range
            // when the browser evicts one it has already handed us.
            const report = (ratio: number) => {
                progress[index] = Math.max(progress[index], asset.weight * ratio);
            };

            switch (asset.kind) {
                case 'video':
                    return preloadVideo(asset.url, report, done);
                case 'image':
                    return preloadImage(asset.url, done);
                case 'scripts':
                    return preloadScripts(done);
                case 'fonts':
                    return preloadFonts(done);
            }
        });

        return () => teardown.forEach(stop => stop());
    }, [stage]);

    return useCallback(() => {
        if (total.current === 0) return 0;
        return loaded.current.reduce((sum, weight) => sum + weight, 0) / total.current;
    }, []);
}
